import React, { useState } from "react";
import { Check, ChevronLeft, X } from "lucide-react";
import AppStatusPill from "@/components/layout/AppStatusPill";
import { Calendar } from "@/components/ui/calendar";
import { Sheet, SheetClose, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { useAppStatusTransitions } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import { AppStatus } from "@/types/borrower";
import { CREATED_PRESETS, createdRangeValue, describeCreated, parseCreated } from "./createdFilter";
import { FilterViewer, offeredFilters, SearchFilter } from "./filterRegistry";
import {
  filterValues,
  joinValueList,
  readFilter,
  SearchToken,
  splitValueList,
  writeFilter,
} from "./searchTokens";
import { optionView } from "./SuggestionList";
import { isRemoteSource, narrowOptions, useFilterOptions } from "./useFilterOptions";

interface FiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  raw: string;
  /** The bar's tokens of `raw`. */
  tokens: SearchToken[];
  onRawChange: (raw: string) => void;
  viewer: FilterViewer;
}

const ROW =
  "flex min-h-14 w-full shrink-0 items-center justify-between gap-4 border-b px-4 text-left text-base";
const TEXT_INPUT =
  "h-12 w-full rounded-md border border-input bg-background px-4 text-base focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/20";
const sameValue = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * Full-screen filter editor opened by press-and-hold on the search bar
 * (touch). Every change is written straight into the search string.
 */
const FiltersSheet: React.FC<FiltersSheetProps> = ({
  open,
  onOpenChange,
  raw,
  tokens,
  onRawChange,
  viewer,
}) => {
  const [screenKey, setScreenKey] = useState<string | null>(null);
  const filters = offeredFilters(viewer);
  const screen = filters.find((filter) => filter.key === screenKey);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) setScreenKey(null);
        onOpenChange(next);
      }}
    >
      <SheetContent
        side="bottom"
        aria-describedby={undefined}
        className="flex h-dvh flex-col gap-0 rounded-none p-0 [&>button]:hidden"
      >
        {screen ? (
          <FilterValueScreen
            key={screen.key}
            filter={screen}
            tokens={tokens}
            onChange={(values, negated, editedSign) =>
              onRawChange(writeFilter(raw, screen, values, negated, editedSign))
            }
            onBack={() => setScreenKey(null)}
          />
        ) : (
          <>
            <ScreenHeader
              title="Filters"
              action={
                <SheetClose className="-ml-1 p-1" aria-label="Close">
                  <X className="h-6 w-6" />
                </SheetClose>
              }
            />
            <div className="flex-1 overflow-y-auto">
              {filters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={ROW}
                  onClick={() => setScreenKey(filter.key)}
                >
                  <span className="shrink-0">{filter.label}</span>
                  <FilterSummary filter={filter} tokens={tokens} />
                </button>
              ))}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

function ScreenHeader({ title, action }: { title: string; action: React.ReactNode }) {
  return (
    <div className="flex h-14 shrink-0 items-center gap-4 border-b px-4">
      {action}
      <SheetTitle className="text-lg font-semibold">{title}</SheetTitle>
    </div>
  );
}

/** The row's current value: included values (status as pills), then excluded ones in red. */
function FilterSummary({ filter, tokens }: { filter: SearchFilter; tokens: SearchToken[] }) {
  const { choices } = useAppStatusTransitions({ enabled: filter.options === "status" });
  const included = filterValues(tokens, filter, false);
  const excluded = filter.excludeParam ? filterValues(tokens, filter, true) : [];
  if (!included.length && !excluded.length) return null;

  const labelOf = (value: string) =>
    filter.options === "created"
      ? describeCreated(value)
      : (choices.find((choice) => choice.value === value)?.label ?? value);
  return (
    <span className="flex min-w-0 flex-wrap items-center justify-end gap-1 text-right">
      {filter.options === "status"
        ? included.map((value) => (
            <AppStatusPill key={value} status={value as AppStatus} choices={choices} />
          ))
        : included.length > 0 && (
            <span className="break-all text-muted-foreground">
              {included.map(labelOf).join(", ")}
            </span>
          )}
      {excluded.length > 0 && (
        <span className="break-all text-destructive">{excluded.map(labelOf).join(", ")}</span>
      )}
    </span>
  );
}

interface FilterValueScreenProps {
  filter: SearchFilter;
  tokens: SearchToken[];
  /** Rewrites the tokens of sign `editedSign` (the screen's sign) with `values` and `negated`. */
  onChange: (values: string[], negated: boolean, editedSign: boolean) => void;
  onBack: () => void;
}

function FilterValueScreen({ filter, tokens, onChange, onBack }: FilterValueScreenProps) {
  const { values, negated } = readFilter(tokens, filter);
  // With both signs in the bar, switching one would merge them into the other.
  const bothSigns = tokens.some((token) => token.filter === filter && token.negated !== negated);
  // Exclude can be switched on before any value is picked.
  const [excludeDraft, setExcludeDraft] = useState(negated);
  const exclude = values.length ? negated : excludeDraft;
  const [query, setQuery] = useState("");

  const excludeRow = filter.excludeParam && !bothSigns && (
    <label className={ROW}>
      Exclude
      <Switch
        checked={exclude}
        onCheckedChange={(checked) => {
          setExcludeDraft(checked);
          if (values.length) onChange(values, checked, negated);
        }}
      />
    </label>
  );

  return (
    <>
      <ScreenHeader
        title={filter.label}
        action={
          <button type="button" className="-ml-1 p-1" aria-label="Back" onClick={onBack}>
            <ChevronLeft className="h-6 w-6" />
          </button>
        }
      />
      <div className="flex flex-1 flex-col overflow-y-auto">
        {filter.options === "none" ? (
          <>
            <TextValue
              filter={filter}
              initial={joinValueList(values)}
              onChange={(text) => onChange(splitValueList(text), exclude, exclude)}
              onDone={onBack}
            />
            {excludeRow}
          </>
        ) : (
          <>
            {isRemoteSource(filter.options) && (
              <ListSearch label={filter.label} query={query} onQueryChange={setQuery} />
            )}
            {excludeRow}
            <ValueOptions
              filter={filter}
              values={values}
              query={query}
              onChange={(next) => onChange(next, exclude, exclude)}
            />
          </>
        )}
      </div>
    </>
  );
}

function TextValue({
  filter,
  initial,
  onChange,
  onDone,
}: {
  filter: SearchFilter;
  initial: string;
  onChange: (text: string) => void;
  onDone: () => void;
}) {
  const [text, setText] = useState(initial);
  return (
    <div className="shrink-0 border-b px-4 py-3">
      <input
        autoFocus
        aria-label={filter.label}
        enterKeyHint="done"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        {...filter.textInput}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          onChange(event.target.value);
        }}
        onKeyDown={(event) => event.key === "Enter" && onDone()}
        className={TEXT_INPUT}
      />
    </div>
  );
}

function OptionRow({
  checked,
  onClick,
  children,
}: {
  checked: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" aria-pressed={checked} className={ROW} onClick={onClick}>
      <span className="flex min-w-0 items-center gap-2">{children}</span>
      {checked && <Check className="h-5 w-5 shrink-0 text-primary" />}
    </button>
  );
}

/** People lists come one page at a time; searching reaches the rest. */
function ListSearch({
  label,
  query,
  onQueryChange,
}: {
  label: string;
  query: string;
  onQueryChange: (query: string) => void;
}) {
  return (
    <div className="shrink-0 border-b px-4 py-3">
      <input
        type="text"
        enterKeyHint="search"
        aria-label={`Search ${label}`}
        placeholder="Search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        className={TEXT_INPUT}
      />
    </div>
  );
}

function ValueOptions({
  filter,
  values,
  query,
  onChange,
}: {
  filter: SearchFilter;
  values: string[];
  /** The remote list's search text; empty for the other lists. */
  query: string;
  onChange: (values: string[]) => void;
}) {
  const { options: fetched, hasMore } = useFilterOptions(filter, query);
  const options = query ? narrowOptions(fetched, query, filter.match, []) : fetched;
  const { choices } = useAppStatusTransitions({ enabled: filter.options === "status" });
  const view = optionView(filter.options);
  const isPicked = (value: string) => values.some((picked) => sameValue(picked, value));
  const isCreated = filter.options === "created";
  const customRange =
    isCreated && values.length > 0 && !CREATED_PRESETS.some((p) => p.value === values[0]);
  // Values typed in the bar that the list does not offer stay visible and removable.
  const unlisted = isCreated
    ? []
    : values.filter((value) => !options.some((o) => sameValue(o.value, value)));

  const toggle = (value: string) => {
    if (!filter.multi) return onChange([value]);
    onChange(isPicked(value) ? values.filter((v) => !sameValue(v, value)) : [...values, value]);
  };

  return (
    <>
      <OptionRow checked={values.length === 0} onClick={() => onChange([])}>
        Any
      </OptionRow>
      {unlisted.map((value) => (
        <OptionRow key={value} checked onClick={() => toggle(value)}>
          <span className="break-all">{value}</span>
        </OptionRow>
      ))}
      {/* On the phone the range calendar sits under the presets, no Custom range row. */}
      {options
        .filter((option) => !option.opensCalendar)
        .map((option) => (
          <OptionRow
            key={option.value}
            checked={isPicked(option.value)}
            onClick={() => toggle(option.value)}
          >
            {view(option, choices)}
          </OptionRow>
        ))}
      {hasMore && options.length > 0 && (
        <p className={cn(ROW, "text-muted-foreground")}>Type to narrow</p>
      )}
      {isCreated && (
        <div className="flex justify-center py-2">
          <Calendar
            mode="range"
            numberOfMonths={1}
            selected={customRange ? parseCreated(values[0]) : undefined}
            onSelect={(range) =>
              range?.from && onChange([createdRangeValue({ from: range.from, to: range.to })])
            }
          />
        </div>
      )}
    </>
  );
}

export default FiltersSheet;
